import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, DueTicketSetting, DueTicketSettingUpdate, DueTicketTeam, Unternehmen } from "@kundeninfo/api-client";
import { Auswahl, Chip, Eingabe, Hinweis, Optionen, Schalter, Tabelle } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { errorProp } from "../../components/fieldErrors.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { Loaded, MasterDataPanel } from "./accounts/masterData.js";
import { eingabeUnlesbar } from "./gemeinsam.js";
import { DUE_TICKET_SOURCES, SUPPLIER_SOURCES } from "../termine/dueItems.js";
import { DUE_ITEM_TEXT } from "../termine/dueItemTexts.js";
import { DUE_TICKET_SETTING_TEXT as T } from "./dueTicketSettingTexts.js";
import "./accounts/accounts.css";

/**
 * VERWALTUNG › FÄLLIGKEITSTICKETS (Terminebene 12, D19, D28; Empfehlung 585): je Unternehmen, ob der Nachtlauf Tickets aus
 * Fälligkeiten anlegt — Schalter, Vorlauf in Tagen, Quellen, Team. Eine Tabelle ohne Objektebene (wie die Teams); Bearbeitet
 * wird im Panel rechts (`MasterDataPanel`), „Speichern“ ist die Bestätigung, „Abbrechen“ mit Eingaben fragt gestapelt.
 *
 * Die Liste kommt mit den aktiven Teams (`teams`, mit `supplierViewComplete`). Mit einer Lieferantenquelle bietet die Auswahl
 * nur Teams mit vollständiger Lieferantensicht an (der Server weist andere mit 422 am Feld `teamId` ab). Die Liste lädt nach dem
 * Schreiben neu und bleibt dabei dasselbe Element (die Scrollposition bleibt); ein fremder Stand (409) lädt über „Neu laden“
 * den aktuellen Stand ins Panel, ein unbekanntes oder stillgelegtes Unternehmen (404) lädt die Liste neu.
 */

export type DueTicketSettingsClient = Pick<ApiClient, "listDueTicketSettings" | "updateDueTicketSetting">;

type Source = DueTicketSetting["sources"][number];
type Draft = { companyId: string; code: string; name: string; version: number; enabled: boolean; leadDays: string;
  sources: Source[]; teamId: string };

const LABELS = { enabled: T.fields.enabled, leadDays: T.fields.leadDays, sources: T.fields.sources, teamId: T.fields.team };
const LEAD_DAYS = /^\d{1,3}$/;

const toDraft = (s: DueTicketSetting): Draft => ({ companyId: s.companyId, code: s.companyShortCode, name: s.companyName,
  version: s.version, enabled: s.enabled, leadDays: String(s.leadDays), sources: [...s.sources], teamId: s.teamId ?? "" });
const hasSupplierSource = (sources: readonly string[]): boolean => sources.some((s) => (SUPPLIER_SOURCES as readonly string[]).includes(s));

/** `companies`: die Unternehmen der Anwendung — der Kürzel-Chip steht in ihrer Hausfarbe (H12, wie überall sonst). */
export function FaelligkeitsticketsBereich(p: { client: DueTicketSettingsClient;
                                              companies: readonly Pick<Unternehmen, "shortCode" | "colorHex">[] }): ReactElement {
  const { client } = p;
  const [rows, setRows] = useState<DueTicketSetting[] | null>(null);
  const [teams, setTeams] = useState<DueTicketTeam[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<{ draft: Draft; start: Draft } | null>(null);
  const clientRef = useRef(client);
  clientRef.current = client;

  /** Liest die Liste neu; die Zeilen bleiben bis zur Antwort stehen (Scrollposition). */
  const reload = useCallback(async (): Promise<DueTicketSetting[]> => {
    const r = await clientRef.current.listDueTicketSettings();
    setRows(r.items);
    setTeams(r.teams);
    return r.items;
  }, []);
  useEffect(() => {
    let current = true;
    reload().catch(() => { if (current) setError(T.loadFailed); });
    return () => { current = false; };
  }, [reload]);

  const open = (s: DueTicketSetting): void => { const d = toDraft(s); setEdit({ draft: d, start: d }); };
  const change = (patch: Partial<Draft>): void => setEdit((e) => (e === null ? e : { ...e, draft: { ...e.draft, ...patch } }));
  const d = edit?.draft ?? null;

  async function save(): Promise<void> {
    if (d === null) return;
    if (!LEAD_DAYS.test(d.leadDays.trim())) throw eingabeUnlesbar([{ field: "leadDays", message: T.leadDaysInvalid }]);
    const body: Omit<DueTicketSettingUpdate, "version"> = { enabled: d.enabled, leadDays: Number(d.leadDays.trim()),
      sources: d.sources, teamId: d.teamId === "" ? null : d.teamId };
    try {
      await client.updateDueTicketSetting(d.companyId, d.version, body);
    } catch (e) {
      // Das Unternehmen ist unbekannt oder stillgelegt: die Liste zeigt es nicht mehr.
      if (e instanceof ApiError && e.status === 404) await reload().catch(() => undefined);
      throw e;
    }
    await reload();
  }
  /** „Neu laden“ nach einem fremden Stand (409): Liste und Entwurf aus dem Serverstand. */
  async function refresh(): Promise<void> {
    const fresh = await reload();
    const row = fresh.find((s) => s.companyId === d?.companyId);
    if (row === undefined) setEdit(null); else open(row);
  }

  const teamName = (s: DueTicketSetting): string => {
    if (s.teamId === null) return T.none;
    return teams.find((t) => t.id === s.teamId)?.name ?? T.teamInactive;
  };
  const incomplete = (s: DueTicketSetting): boolean => s.enabled && hasSupplierSource(s.sources)
    && teams.find((t) => t.id === s.teamId)?.supplierViewComplete === false;

  return (
    <div className="vwk">
      <Loaded list={{ rows, error }}>{(list) => (list.length === 0 ? <p className="vw__hinweis">{T.empty}</p> : (
        <Tabelle stapeln>
          <table className="vwk__tabelle">
            <thead>
              <tr>
                <th scope="col">{T.columns.company}</th>
                <th scope="col">{T.columns.state}</th>
                <th scope="col" data-ausrichtung="rechts">{T.columns.leadDays}</th>
                <th scope="col">{T.columns.sources}</th>
                <th scope="col">{T.columns.team}</th>
                <th scope="col" aria-label={T.edit} />
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.companyId}>
                  <td><span className="vwk__zelle">
                    <Chip {...hausfarbeChip(p.companies, s.companyShortCode)}>{s.companyShortCode}</Chip>{s.companyName}
                  </span></td>
                  <td><span className="vwk__zelle">
                    {s.enabled ? <Chip statusArt="ok">{T.on(s.enabledFrom)}</Chip> : <Chip>{T.off}</Chip>}
                  </span></td>
                  <td data-ausrichtung="rechts">{T.leadDaysShort(s.leadDays)}</td>
                  <td><span className="vwk__zelle">
                    {s.sources.length === 0 ? T.none : s.sources.map((q) => <Chip key={q}>{DUE_ITEM_TEXT.chipLabel[q]}</Chip>)}
                  </span></td>
                  <td><span className="vwk__zelle">
                    {teamName(s)}
                    {incomplete(s) && <Chip statusArt="warnung">{T.supplierViewIncomplete}</Chip>}
                  </span></td>
                  <td><KiKnopf kompakt name={T.editNamed(s.companyShortCode)} onClick={() => open(s)}>{T.edit}</KiKnopf></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tabelle>
      ))}</Loaded>

      {d !== null && edit !== null && (
        <MasterDataPanel title={T.panelTitle(d.code, d.name)} name={d.name} dirty={JSON.stringify(d) !== JSON.stringify(edit.start)}
          deactivating={false} save={save} refresh={refresh} onClose={() => setEdit(null)} labels={LABELS}>
          {(f) => {
            const supplier = hasSupplierSource(d.sources);
            const choices = teams.filter((t) => !supplier || t.supplierViewComplete);
            const known = teams.find((t) => t.id === d.teamId);
            // Das gewählte Team bleibt lesbar, auch wenn es nicht (mehr) wählbar ist; die Wahl selbst weist der Server ab.
            const extra = d.teamId === "" || choices.some((t) => t.id === d.teamId) ? null
              : { id: d.teamId, text: known === undefined ? T.teamInactive : T.teamIncomplete(known.name) };
            return (
              <>
                <Hinweis art="info">{T.hint}</Hinweis>
                <Schalter beschriftung={T.fields.enabled} an={d.enabled} {...errorProp(f.enabled)}
                  onGeaendert={(e) => change({ enabled: e.an })} />
                <Eingabe beschriftung={T.fields.leadDays} art="dezimal" inputMode="numeric" einheit={T.leadDaysUnit}
                  wert={d.leadDays} {...errorProp(f.leadDays)} onGeaendert={(e) => change({ leadDays: e.wert })} />
                <Optionen beschriftung={T.fields.sources} mehrfach wert={d.sources} {...errorProp(f.sources)}
                  // Typverengung: `Optionen` liefert Zeichenketten; nur bekannte Quellen (in fester Folge) gehen in den Entwurf.
                  onGeaendert={(e) => change({ sources: DUE_TICKET_SOURCES.filter((q) => (e.werte ?? []).includes(q)) })}>
                  {DUE_TICKET_SOURCES.map((q) => <option key={q} value={q}>{DUE_ITEM_TEXT.chipLabel[q]}</option>)}
                </Optionen>
                <Auswahl beschriftung={T.fields.team} wert={d.teamId} {...(supplier ? { hilfe: T.teamHelp } : {})}
                  {...errorProp(f.teamId)} onGeaendert={(e) => change({ teamId: e.wert })}>
                  <option value="">{T.noTeam}</option>
                  {choices.map((t) => <option key={t.id} value={t.id}>{`${t.shortCode} ${t.name}`}</option>)}
                  {extra !== null && <option value={extra.id}>{extra.text}</option>}
                </Auswahl>
              </>
            );
          }}
        </MasterDataPanel>
      )}
    </div>
  );
}
